import { CARCASS_MM, HALF_CARCASS_MM } from "./carcass";
import { clamp, snap, STEP } from "./project";
import type { InternalItem, WardrobeModule } from "../types";
import { cabinetBaseHeightMm } from "./installation";
import { cornerItemPosition } from "./cornerInternals";
import { defaultDividerHeightMm, interiorVerticalRangeMm, itemsShareCompartment, usableItemPieces } from "./compartments";
import { cabinetInteriorFloorMm } from "./cabinetDrawers";
import { fittingVerticalOffsets } from "./wardrobeFittings";

const MIN_VERTICAL_GAP = 32;
const MIN_DIVIDER_HEIGHT = 320;
const DIVIDER_TOP_CLEARANCE = CARCASS_MM;
const SHELF_HALF_THICKNESS = HALF_CARCASS_MM;
const DIVIDER_SHELF_SNAP_TOLERANCE = 24;
const snapDown = (value: number) => Math.floor(value / STEP) * STEP;
const snapUp = (value: number) => Math.ceil(value / STEP) * STEP;

const dividerShelfItems = (
  module: WardrobeModule,
  dividerId: string
) =>
  module.items.filter(
    (item) =>
      item.id !== dividerId &&
      (item.type === "shelf" || item.type === "led")
  );

const nearestWithin = (
  value: number,
  candidates: number[],
  tolerance: number
) => {
  const nearest = candidates
    .filter(Number.isFinite)
    .sort(
      (first, second) =>
        Math.abs(first - value) - Math.abs(second - value)
    )[0];
  return nearest !== undefined && Math.abs(nearest - value) <= tolerance
    ? nearest
    : value;
};

export const dividerMaxHeightFromBottom = (
  moduleHeight: number,
  bottom: number
) =>
  Math.max(
    MIN_DIVIDER_HEIGHT,
    Math.floor(moduleHeight - DIVIDER_TOP_CLEARANCE - bottom)
  );

export const snapDividerHeightToShelves = (
  module: WardrobeModule,
  dividerId: string,
  currentY: number,
  currentHeight: number,
  desiredHeight: number
) => {
  const bottom = currentY - currentHeight / 2;
  const desiredTop = bottom + desiredHeight;
  const shelfUndersides = dividerShelfItems(module, dividerId)
    .map((shelf) => shelf.y - SHELF_HALF_THICKNESS)
    .filter((underside) => underside - bottom >= MIN_DIVIDER_HEIGHT);
  const snappedTop = nearestWithin(
    desiredTop,
    shelfUndersides,
    DIVIDER_SHELF_SNAP_TOLERANCE
  );
  return snappedTop - bottom;
};

const snapDividerYToShelves = (
  module: WardrobeModule,
  divider: InternalItem,
  desiredY: number,
  minY: number,
  maxY: number
) => {
  const height = divider.height ?? Math.min(1400, module.height - 180);
  const halfHeight = height / 2;
  const contactCenters = dividerShelfItems(module, divider.id).flatMap(
    (shelf) => [
      shelf.y - SHELF_HALF_THICKNESS - halfHeight,
      shelf.y + SHELF_HALF_THICKNESS + halfHeight
    ]
  );
  return clamp(
    nearestWithin(
      desiredY,
      contactCenters,
      DIVIDER_SHELF_SNAP_TOLERANCE
    ),
    minY,
    maxY
  );
};

export const resizeDividerFromBottom = (
  moduleHeight: number,
  currentY: number,
  currentHeight: number,
  desiredHeight: number
) => {
  const bottom = currentY - currentHeight / 2;
  const maxHeight = dividerMaxHeightFromBottom(moduleHeight, bottom);
  const height = clamp(
    Math.round(desiredHeight),
    MIN_DIVIDER_HEIGHT,
    maxHeight
  );

  return {
    bottom,
    height,
    maxHeight,
    y: bottom + height / 2
  };
};

const itemOffsets = (item: InternalItem): [number, number] => {
  if (item.type === "shelf") return [-HALF_CARCASS_MM, HALF_CARCASS_MM];
  if (item.type === "led") return [-6, 6];
  if (item.type === "rail") return [-14, 14];
  if (item.type === "drawer") return [-(item.height ?? 180), 0];
  if (item.type === "basket") return [-(item.height ?? 160), 0];
  const fitting = fittingVerticalOffsets(item.type, item.height);
  if (fitting) return fitting;
  if (item.type === "pantograph") return [-360, 0];
  return [-HALF_CARCASS_MM, HALF_CARCASS_MM];
};

/** Дно для наполнения: верх дна или неподвижной полки над наружными ящиками. */
const interiorFloorTop = (module: WardrobeModule) => cabinetInteriorFloorMm(module);

const interiorBottomClearance = (module: WardrobeModule) =>
  Math.max(80, interiorFloorTop(module));

/** Где по высоте может стоять перегородка: от дна над цоколем до крыши. */
export const dividerSpanLimits = (module: WardrobeModule) => ({
  bottom: interiorBottomClearance(module),
  top: module.height - CARCASS_MM,
  minHeight: MIN_DIVIDER_HEIGHT
});

/*
 * Перегородка — всегда от опоры до опоры (приёмка 22.10, Сергей): стоит на дне
 * или на полке и упирается в полку или крышу. Висеть над корзиной не может.
 * Промежуток между соседними опорами — «пролёт». Перегородка занимает один
 * или несколько пролётов подряд; полка, через которую она проходит, делится
 * на две половины. Пролёт, где стоит ящик, корзина, брючница или пантограф,
 * занят: перегородка через него не проходит.
 */
const DIVIDER_BLOCKERS = new Set<InternalItem["type"]>(["drawer", "basket", "leatherBasket", "shoeRack", "trouserRack", "pantograph"]);
const MIN_DIVIDER_BAY_MM = 60;

export interface DividerBay { from: number; to: number; blocked: boolean }
export interface DividerSpan { bottom: number; top: number; height: number; y: number }

/** Пролёты по высоте там, где стоит перегородка (по оси x её отсека). */
export const dividerBays = (module: WardrobeModule, divider: InternalItem, x = divider.x ?? 0): DividerBay[] => {
  const plain: WardrobeModule = { ...module, items: module.items.filter((item) => item.type !== "divider") };
  const covers = (item: InternalItem) =>
    usableItemPieces(plain, item).some((piece) => x >= piece.left - 0.5 && x <= piece.right + 0.5);
  const [floor, roof] = interiorVerticalRangeMm(module);
  const shelves = plain.items
    .filter((item) => (item.type === "shelf" || item.type === "led") && covers(item))
    .map((item) => item.y)
    .filter((y) => y - SHELF_HALF_THICKNESS > floor + 1 && y + SHELF_HALF_THICKNESS < roof - 1)
    .sort((first, second) => first - second);
  const blockers = plain.items
    .filter((item) => DIVIDER_BLOCKERS.has(item.type) && covers(item))
    .map((item) => {
      const [low, high] = itemOffsets(item);
      return [item.y + low, item.y + high] as const;
    });
  const bays: DividerBay[] = [];
  let from = floor;
  [...shelves, null].forEach((shelf) => {
    const to = shelf === null ? roof : shelf - SHELF_HALF_THICKNESS;
    if (to - from >= MIN_DIVIDER_BAY_MM) {
      bays.push({ from, to, blocked: blockers.some(([low, high]) => high > from + 0.5 && low < to - 0.5) });
    }
    if (shelf !== null) from = shelf + SHELF_HALF_THICKNESS;
  });
  return bays;
};

const spanOf = (bottom: number, top: number): DividerSpan => ({ bottom, top, height: top - bottom, y: (bottom + top) / 2 });

/** Свободные окна пролётов подряд: [первый, последний]. */
const freeWindows = (bays: DividerBay[], length?: number) => {
  const windows: Array<[number, number]> = [];
  for (let first = 0; first < bays.length; first += 1) {
    for (let last = first; last < bays.length; last += 1) {
      if (bays[last]!.blocked) break;
      if (length === undefined || last - first + 1 === length) windows.push([first, last]);
    }
  }
  return windows;
};

/** Ближайшие опоры к нынешним краям перегородки; null — поставить некуда. */
const fittedWindow = (module: WardrobeModule, divider: InternalItem, bays = dividerBays(module, divider)) => {
  const height = divider.height ?? defaultDividerHeightMm(module);
  const bottom = divider.y - height / 2;
  const top = divider.y + height / 2;
  let best: { window: [number, number]; score: number } | null = null;
  for (const window of freeWindows(bays)) {
    const score = Math.abs(bays[window[0]]!.from - bottom) + Math.abs(bays[window[1]]!.to - top);
    if (!best || score < best.score - 0.001) best = { window, score };
  }
  return best?.window ?? null;
};

/** Перегородка, прижатая к опорам: так её хранит проект. */
export const fitDividerToSupports = (module: WardrobeModule, divider: InternalItem): DividerSpan | null => {
  const bays = dividerBays(module, divider);
  const window = fittedWindow(module, divider, bays);
  return window ? spanOf(bays[window[0]]!.from, bays[window[1]]!.to) : null;
};

/**
 * Перетаскивание целиком: перегородка перескакивает в соседние пролёты и
 * сама растягивается или сжимается под них — столько же пролётов, сколько
 * занимала (если так нельзя — один пролёт).
 */
export const resolveDividerMove = (module: WardrobeModule, divider: InternalItem, desiredY: number, x = divider.x ?? 0): DividerSpan | null => {
  const current = fittedWindow(module, divider);
  const length = current ? current[1] - current[0] + 1 : 1;
  const bays = dividerBays(module, divider, x);
  let windows = freeWindows(bays, length);
  if (!windows.length) windows = freeWindows(bays, 1);
  let best: { span: DividerSpan; score: number } | null = null;
  for (const [first, last] of windows) {
    const span = spanOf(bays[first]!.from, bays[last]!.to);
    const score = Math.abs(span.y - desiredY);
    if (!best || score < best.score - 0.001) best = { span, score };
  }
  return best?.span ?? null;
};

/**
 * Край перегородки двигается от опоры к опоре (приёмка 22.10): низ — на
 * полку ниже или выше, верх — так же. Через занятый пролёт край не проходит.
 */
export const resolveDividerEdge = (
  module: WardrobeModule,
  divider: InternalItem,
  edge: "bottom" | "top",
  value: number
): DividerSpan => {
  const bays = dividerBays(module, divider);
  const window = fittedWindow(module, divider, bays);
  const height = divider.height ?? defaultDividerHeightMm(module);
  if (!window) return spanOf(divider.y - height / 2, divider.y + height / 2);
  let [first, last] = window;
  if (edge === "bottom") {
    let best = first;
    for (let candidate = last; candidate >= 0; candidate -= 1) {
      if (bays[candidate]!.blocked) break;
      if (Math.abs(bays[candidate]!.from - value) < Math.abs(bays[best]!.from - value)) best = candidate;
    }
    first = best;
  } else {
    let best = last;
    for (let candidate = first; candidate < bays.length; candidate += 1) {
      if (bays[candidate]!.blocked) break;
      if (Math.abs(bays[candidate]!.to - value) < Math.abs(bays[best]!.to - value)) best = candidate;
    }
    last = best;
  }
  return spanOf(bays[first]!.from, bays[last]!.to);
};

/** Куда можно передвинуть края на одну опору — для кнопок в панели. */
export const dividerEdgeSteps = (module: WardrobeModule, divider: InternalItem) => {
  const bays = dividerBays(module, divider);
  const window = fittedWindow(module, divider, bays);
  if (!window) return null;
  const [first, last] = window;
  const free = (index: number) => index >= 0 && index < bays.length && !bays[index]!.blocked;
  return {
    bottomDown: free(first - 1) ? bays[first - 1]!.from : null,
    bottomUp: first < last ? bays[first + 1]!.from : null,
    topDown: first < last ? bays[last - 1]!.to : null,
    topUp: free(last + 1) ? bays[last + 1]!.to : null,
    blockedBelow: first > 0 && bays[first - 1]!.blocked,
    blockedAbove: last < bays.length - 1 && bays[last + 1]!.blocked
  };
};

export const internalItemMinimumY = (
  module: WardrobeModule,
  item: InternalItem
) => {
  if (item.type === "divider") {
    const height = item.height ?? Math.min(1400, module.height - 180);
    return interiorBottomClearance(module) + height / 2;
  }

  const [itemMin] = itemOffsets(item);
  return snapUp(
    Math.max(interiorBottomClearance(module), interiorFloorTop(module) - itemMin)
  );
};

const collides = (
  candidateY: number,
  moving: InternalItem,
  blocker: InternalItem
) => {
  if (moving.type === "divider" || blocker.type === "divider") return false;
  const [movingMin, movingMax] = itemOffsets(moving);
  const [blockerMin, blockerMax] = itemOffsets(blocker);
  return (
    candidateY + movingMax > blocker.y + blockerMin - MIN_VERTICAL_GAP &&
    candidateY + movingMin < blocker.y + blockerMax + MIN_VERTICAL_GAP
  );
};

export const resolveInternalItemY = (
  module: WardrobeModule,
  itemId: string,
  desiredY: number,
  directionHint = 0
) => {
  const moving = module.items.find((item) => item.id === itemId);
  if (!moving) return snap(desiredY);
  if (module.type === "corner45") return cornerItemPosition(module, moving, desiredY) ?? moving.y;

  if (moving.type === "divider") {
    return resolveDividerMove(module, moving, desiredY)?.y ?? moving.y;
  }

  const [movingMin, movingMax] = itemOffsets(moving);
  const minY = internalItemMinimumY(module, moving);
  const maxY = Math.max(minY, module.height - CARCASS_MM - movingMax);
  const desired = clamp(snap(desiredY), minY, maxY);
  // Мешают только детали того же отсека: ящик справа не упирается в ящик слева.
  const placed = { ...moving, y: desired };
  const blockers = module.items.filter((item) => item.id !== itemId &&
    itemsShareCompartment(module, placed, item));

  const candidates = new Set<number>([
    desired,
    minY,
    maxY,
    clamp(moving.y, minY, maxY)
  ]);
  blockers.forEach((blocker) => {
    const [blockerMin, blockerMax] = itemOffsets(blocker);
    const below =
      blocker.y + blockerMin - MIN_VERTICAL_GAP - movingMax;
    const above =
      blocker.y + blockerMax + MIN_VERTICAL_GAP - movingMin;
    candidates.add(clamp(snapDown(below), minY, maxY));
    candidates.add(clamp(snapUp(above), minY, maxY));
  });

  for (
    let candidate = snapUp(minY);
    candidate <= maxY;
    candidate += STEP
  ) {
    candidates.add(candidate);
  }

  const valid = [...candidates].filter((candidate) =>
    blockers.every((blocker) => !collides(candidate, moving, blocker))
  );
  if (!valid.length) return desired;

  const direction = Math.sign(directionHint);
  const directional =
    direction > 0
      ? valid.filter((candidate) => candidate >= desired)
      : direction < 0
        ? valid.filter((candidate) => candidate <= desired)
        : valid;
  const ranked = directional.length ? directional : valid;

  return ranked.sort((a, b) => {
    const distanceA = Math.abs(a - desired);
    const distanceB = Math.abs(b - desired);
    if (distanceA !== distanceB) return distanceA - distanceB;
    return direction >= 0 ? b - a : a - b;
  })[0]!;
};

/**
 * Куда встаёт деталь, которую тянут вверх или вниз (direction — знак движения
 * курсора, current — где она сейчас). Как resolveInternalItemY, но через
 * полку, ящик или пантограф не перепрыгивает, пока курсор до них не дошёл:
 * останавливается у препятствия и перескакивает, только когда курсор уже по
 * ту сторону и место там свободно (видео Сергея 31.10: штанга «пропала
 * куда-то, опять появилась»).
 */
export const dragInternalItemY = (
  module: WardrobeModule,
  itemId: string,
  desiredY: number,
  direction: number,
  current: number
) => {
  const next = resolveInternalItemY(module, itemId, desiredY, direction);
  const moving = module.items.find((item) => item.id === itemId);
  const sign = Math.sign(direction);
  if (!moving || moving.type === "divider" || module.type === "corner45" || sign === 0) return next;
  const desired = snap(desiredY);
  if ((next - desired) * sign <= STEP) return next;
  const before = resolveInternalItemY(module, itemId, desired, -sign);
  return (before - current) * sign >= 0 && (desired - before) * sign >= 0 ? before : current;
};

/** Внутренний ящик (31.10): высота 250–450 мм — не ниже правила цеха, не выше половины обычного отсека. */
export const INTERNAL_DRAWER_MIN_HEIGHT_MM = 250;
export const INTERNAL_DRAWER_MAX_HEIGHT_MM = 450;

/**
 * Новая высота внутреннего ящика: низ остаётся на месте (с точностью до шага
 * 32 мм), ящик растёт вверх; упирается в полку или ящик выше — опускается,
 * но только в своём промежутке: через полку или соседний ящик не перепрыгивает.
 * null — в этом промежутке ящик такой высоты не помещается.
 */
export const resizeInternalDrawer = (module: WardrobeModule, itemId: string, height: number) => {
  const drawer = module.items.find((item) => item.id === itemId);
  if (!drawer || drawer.type !== "drawer") return null;
  const nextHeight = Math.round(clamp(height, INTERNAL_DRAWER_MIN_HEIGHT_MM, INTERNAL_DRAWER_MAX_HEIGHT_MM));
  const [currentMin] = itemOffsets(drawer);
  const bottom = drawer.y + currentMin;
  const middle = drawer.y + currentMin / 2;
  const resized: InternalItem = { ...drawer, height: nextHeight };
  const trial: WardrobeModule = { ...module, items: module.items.map((item) => item.id === itemId ? resized : item) };
  const blockers = trial.items.filter((item) => item.id !== itemId && item.type !== "divider" &&
    itemsShareCompartment(trial, drawer, item));
  // Промежуток ящика: от ближайшей детали ниже до ближайшей детали выше.
  let ceiling = module.height - CARCASS_MM;
  let floor = -Infinity;
  blockers.forEach((blocker) => {
    const [blockerMin, blockerMax] = itemOffsets(blocker);
    if (blocker.y + (blockerMin + blockerMax) / 2 >= middle) ceiling = Math.min(ceiling, blocker.y + blockerMin - MIN_VERTICAL_GAP);
    else floor = Math.max(floor, blocker.y + blockerMax + MIN_VERTICAL_GAP);
  });
  const lowest = Math.max(internalItemMinimumY(trial, resized), snapUp(floor + nextHeight));
  const highest = snapDown(ceiling);
  if (lowest > highest) return null;
  const y = clamp(snap(bottom + nextHeight), lowest, highest);
  return { y, height: nextHeight };
};
